"use client"

import { getExportRequestDetails } from "@tc/ui-dfe/query/queries/export"
import { getTransferRequestDetails } from "@tc/ui-dfe/query/queries/transfer"
import {
  getActiveTemplateBySubmissionLayout,
  getDfeFormConfig,
  GetRequestValidations,
  getTemplateWithApprovedVersion,
} from "@tc/ui-dfe/query/queries/common"
import {
  CoreRequestType,
  CoreStatus,
  ExportStatus,
  SubmissionLayoutType,
} from "@tc/ui-dfe/query/types"
import { useClient } from "@urql/next"
import { useCallback, useRef } from "react"

type PrefetchRequestDetails = {
  templateId?: string | null
  template?: { id?: string | null } | null
}

type PrefetchExportDetailsData = {
  getExportReqDetails?: PrefetchRequestDetails & {
    status?: ExportStatus | null
  }
}

type PrefetchTransferDetailsData = {
  getTransferRequestDetails?: PrefetchRequestDetails & {
    status?: { code?: CoreStatus | null } | null
  }
}

const PREFETCH_TTL_MS = 5000
const MAX_CACHE_SIZE = 100
const CLEANUP_THRESHOLD = 50
const MAX_CONCURRENT_PREFETCHES = 3

const prefetchTimestamps = new Map<string, number>()
const inFlightPrefetches = new Map<string, Promise<unknown>>()

/**
 * Clean up stale entries from prefetchTimestamps.
 * Called when map size exceeds threshold to prevent memory leaks.
 */
function cleanupStalePrefetches(): void {
  const now = Date.now()
  for (const [key, timestamp] of prefetchTimestamps) {
    if (now - timestamp >= PREFETCH_TTL_MS) {
      prefetchTimestamps.delete(key)
    }
  }

  // If still too large after TTL cleanup, remove oldest entries
  if (prefetchTimestamps.size > MAX_CACHE_SIZE) {
    const entries = [...prefetchTimestamps.entries()].sort(
      (a, b) => a[1] - b[1],
    )
    const toRemove = entries.slice(0, entries.length - MAX_CACHE_SIZE)
    toRemove.forEach(([key]) => prefetchTimestamps.delete(key))
  }
}

/**
 * Check if a request was prefetched recently (within TTL).
 * If true, use cache-first policy for instant load.
 */
export function wasPrefetchedRecently(
  type: CoreRequestType,
  id: string,
): boolean {
  const key = `${type}-${id}`
  const timestamp = prefetchTimestamps.get(key)
  if (!timestamp) return false
  return Date.now() - timestamp < PREFETCH_TTL_MS
}

/**
 * Invalidate prefetch cache for a specific request.
 * Call this after mutations (save/submit) to ensure fresh data on next load.
 */
export function invalidatePrefetch(type: CoreRequestType, id: string): void {
  const key = `${type}-${id}`
  prefetchTimestamps.delete(key)
}

/**
 * Get in-flight prefetch promise if one exists.
 * Allows page load to wait for prefetch instead of making duplicate call.
 */
export function getInFlightPrefetch(
  type: CoreRequestType,
  id: string,
): Promise<unknown> | undefined {
  return inFlightPrefetches.get(`${type}-${id}`)
}

/** Statuses where validation prefetch is skipped (handled by submit flow) */
const EDITABLE_EXPORT_STATUSES: (ExportStatus | null)[] = [
  ExportStatus.Draft,
  ExportStatus.Resubmit,
]
const EDITABLE_TRANSFER_STATUSES: (CoreStatus | null)[] = [
  CoreStatus.Draft,
  CoreStatus.Resubmit,
  CoreStatus.ToBeAmended,
]

/**
 * Hook for prefetching request data AND dependent queries on hover/focus.
 *
 * Prefetch Flow:
 * 1. Fetch request details (export/transfer) - MUST be first (contains templateId)
 * 2. Extract templateId, templateVersionId, status from response
 * 3. Fire dependent queries IN PARALLEL:
 *    - getTemplateWithApprovedVersion (for template change banner)
 *    - getDfeFormConfig (form fields/recipe)
 *    - getRequestValidations (for non-editable statuses only)
 *
 * Why Sequential First Step?
 * - getDfeFormConfig requires templateId + templateVersionId as parameters
 * - These values only exist in the request details response
 * - The list page only has requestId, not template info
 * - Therefore: details fetch → extract params → parallel dependent fetches
 *
 * All fetched data goes into URQL cache for instant page transitions.
 */
export function usePrefetchRequest() {
  const apiClient = useClient()
  const prefetchedIds = useRef<Set<string>>(new Set())

  const prefetchExport = useCallback(
    (requestId: string) => {
      const cacheKey = `${CoreRequestType.Export}-${requestId}`
      // Skip if already prefetched AND still within TTL
      if (
        prefetchedIds.current.has(cacheKey) &&
        wasPrefetchedRecently(CoreRequestType.Export, requestId)
      )
        return

      // Limit concurrent prefetches to prevent memory pressure on rapid scrolling
      if (inFlightPrefetches.size >= MAX_CONCURRENT_PREFETCHES) return

      // Cleanup stale entries if threshold exceeded
      if (prefetchTimestamps.size > CLEANUP_THRESHOLD) {
        cleanupStalePrefetches()
      }

      prefetchedIds.current.add(cacheKey)
      prefetchTimestamps.set(cacheKey, Date.now())

      const promise = (async () => {
        try {
          // Step 1: Fetch export request details (required to get templateId)
          const result = await apiClient
            .query(getExportRequestDetails.toString(), { id: requestId })
            .toPromise()

          const data = result.data as PrefetchExportDetailsData | undefined
          if (result.error || !data?.getExportReqDetails) {
            console.warn("Prefetch failed (export details)", {
              requestId,
              error: result.error?.message,
            })
            prefetchTimestamps.delete(cacheKey)
            prefetchedIds.current.delete(cacheKey)
            return
          }

          const details = data.getExportReqDetails
          const { templateId, template, status } = details
          const templateVersionId = template?.id

          // Step 2: Fire dependent prefetches IN PARALLEL
          const dependentPrefetches: Promise<unknown>[] = []

          // Template approved version
          if (templateId) {
            dependentPrefetches.push(
              apiClient
                .query(getTemplateWithApprovedVersion.toString(), {
                  templateId,
                })
                .toPromise()
                .catch((err) => {
                  console.warn("Prefetch failed (template version)", {
                    requestId,
                    error: err?.message,
                  })
                }),
            )
          }

          // DFE Form Config (recipe/fields)
          if (templateId && templateVersionId) {
            dependentPrefetches.push(
              apiClient
                .query(getDfeFormConfig.toString(), {
                  templateId,
                  templateVersionId,
                })
                .toPromise()
                .catch((err) => {
                  console.warn("Prefetch failed (form config)", {
                    requestId,
                    error: err?.message,
                  })
                }),
            )
          }

          // Request validations (only for view page - non-editable statuses)
          if (status && !EDITABLE_EXPORT_STATUSES.includes(status)) {
            dependentPrefetches.push(
              apiClient
                .query(GetRequestValidations.toString(), {
                  input: {
                    requestId,
                    requestType: CoreRequestType.Export,
                    skipFieldValidation: false,
                  },
                })
                .toPromise()
                .catch((err) => {
                  console.warn("Prefetch failed (validations)", {
                    requestId,
                    error: err?.message,
                  })
                }),
            )
          }

          // Fire dependent prefetches - don't await, let them populate URQL cache in background
          void Promise.allSettled(dependentPrefetches)
        } catch (error) {
          console.warn("Prefetch failed (export)", { requestId, error })
          prefetchTimestamps.delete(cacheKey)
          prefetchedIds.current.delete(cacheKey)
        }
      })()

      inFlightPrefetches.set(cacheKey, promise)
      void promise.finally(() => inFlightPrefetches.delete(cacheKey))
    },
    [apiClient],
  )

  const prefetchTransfer = useCallback(
    (requestId: string) => {
      const cacheKey = `${CoreRequestType.Transfer}-${requestId}`
      // Skip if already prefetched AND still within TTL
      if (
        prefetchedIds.current.has(cacheKey) &&
        wasPrefetchedRecently(CoreRequestType.Transfer, requestId)
      )
        return

      // Limit concurrent prefetches to prevent memory pressure on rapid scrolling
      if (inFlightPrefetches.size >= MAX_CONCURRENT_PREFETCHES) return

      // Cleanup stale entries if threshold exceeded
      if (prefetchTimestamps.size > CLEANUP_THRESHOLD) {
        cleanupStalePrefetches()
      }

      prefetchedIds.current.add(cacheKey)
      prefetchTimestamps.set(cacheKey, Date.now())

      const promise = (async () => {
        try {
          // Step 1: Fetch transfer request details (required to get templateId)
          const result = await apiClient
            .query(getTransferRequestDetails.toString(), { id: requestId })
            .toPromise()

          const data = result.data as PrefetchTransferDetailsData | undefined
          if (result.error || !data?.getTransferRequestDetails) {
            console.warn("Prefetch failed (transfer details)", {
              requestId,
              error: result.error?.message,
            })
            prefetchTimestamps.delete(cacheKey)
            prefetchedIds.current.delete(cacheKey)
            return
          }

          const details = data.getTransferRequestDetails
          const { templateId, template, status } = details
          const templateVersionId = template?.id
          const statusCode = status?.code

          // Step 2: Fire dependent prefetches IN PARALLEL
          const dependentPrefetches: Promise<unknown>[] = []

          // Active template
          dependentPrefetches.push(
            apiClient
              .query(getActiveTemplateBySubmissionLayout.toString(), {
                submissionLayout: SubmissionLayoutType.ApTransfer,
              })
              .toPromise()
              .catch((err) => {
                console.warn("Prefetch failed (active template)", {
                  requestId,
                  error: err?.message,
                })
              }),
          )

          // DFE Form Config (recipe/fields)
          if (templateId && templateVersionId) {
            dependentPrefetches.push(
              apiClient
                .query(getDfeFormConfig.toString(), {
                  templateId,
                  templateVersionId,
                })
                .toPromise()
                .catch((err) => {
                  console.warn("Prefetch failed (form config)", {
                    requestId,
                    error: err?.message,
                  })
                }),
            )
          }

          // Request validations (only for view page - non-editable statuses)
          if (statusCode && !EDITABLE_TRANSFER_STATUSES.includes(statusCode)) {
            dependentPrefetches.push(
              apiClient
                .query(GetRequestValidations.toString(), {
                  input: {
                    requestId,
                    requestType: CoreRequestType.Transfer,
                    skipFieldValidation: false,
                  },
                })
                .toPromise()
                .catch((err) => {
                  console.warn("Prefetch failed (validations)", {
                    requestId,
                    error: err?.message,
                  })
                }),
            )
          }

          // Fire dependent prefetches - don't await, let them populate URQL cache in background
          void Promise.allSettled(dependentPrefetches)
        } catch (error) {
          console.warn("Prefetch failed (transfer)", { requestId, error })
          prefetchTimestamps.delete(cacheKey)
          prefetchedIds.current.delete(cacheKey)
        }
      })()

      inFlightPrefetches.set(cacheKey, promise)
      void promise.finally(() => inFlightPrefetches.delete(cacheKey))
    },
    [apiClient],
  )

  return { prefetchExport, prefetchTransfer }
}
